// Application shell: header, sidebars, editor/preview, autosave, live events.
import { ApiError, api } from './api.js';
import { confirmDialog, toast } from './modals.js';
import { debounce, h } from './dom.js';
import { FileTree, createNote, uploadFiles } from './files.js';
import { HistoryPane } from './history.js';
import { GitPanel } from './git.js';
import { GraphView } from './graph.js';
import { LinkPane } from './links.js';
import { MarkdownEditor } from './editor.js';
import { QuickSwitcher } from './quickopen.js';
import { SearchPane, TagsPane } from './search.js';
import { SettingsPanel } from './settings.js';
import { TabbedPanel } from './tabs.js';
import { buildWikiIndex, renderMarkdown, type WikiIndex } from './preview.js';
import { showConflictDialog } from './conflicts.js';
import type { AuthMe, FileResponse, SaveState, TagCount, TreeNode } from './types.js';

export type ViewMode = 'edit' | 'preview' | 'split';
export type ThemeMode = 'system' | 'light' | 'dark';

const THEME_KEY = 'vaultsync.theme';
const VIEW_KEY = 'vaultsync.view';
const AUTOSAVE_MS = 700;

function flattenTree(node: TreeNode, out: string[] = []): string[] {
  for (const child of node.children ?? []) {
    if (child.type === 'dir') flattenTree(child, out);
    else out.push(child.path);
  }
  return out;
}

const SAVE_LABELS: Record<SaveState, string> = {
  none: 'no file',
  saved: 'saved',
  unsaved: 'unsaved',
  saving: 'saving…',
  conflict: 'conflict',
};

/** The whole SPA. Owns the DOM, the open file session and refresh orchestration. */
export class App {
  readonly api = api;
  readonly root: HTMLElement;
  user: string | null;
  vaultName: string;
  rev = 0;
  filePaths: string[] = [];
  tagList: TagCount[] = [];
  current: string | null = null;
  saveState: SaveState = 'none';

  private wikiIndex: WikiIndex = buildWikiIndex([]);
  private baseHash: string | null = null;
  private localRev = 0;
  private savedContent = '';
  private theme: ThemeMode = 'system';
  private viewMode: ViewMode = 'split';

  private readonly vaultEl: HTMLElement;
  private readonly revEl: HTMLElement;
  private readonly saveEl: HTMLElement;
  private readonly noteTitleEl: HTMLElement;
  private readonly notePathEl: HTMLElement;
  private readonly viewButtons = new Map<ViewMode, HTMLButtonElement>();
  private readonly tree: FileTree;
  private readonly searchPane: SearchPane;
  private readonly tagsPane: TagsPane;
  private readonly backlinksPane: LinkPane;
  private readonly outlinksPane: LinkPane;
  private readonly historyPane: HistoryPane;
  private readonly graph: GraphView;
  private readonly rightPanel: TabbedPanel;
  private readonly leftPanel: TabbedPanel;
  private readonly editor: MarkdownEditor;
  private readonly editorHost: HTMLElement;
  private readonly previewEl: HTMLElement;
  private readonly centerEl: HTMLElement;
  private readonly quickSwitcher: QuickSwitcher;
  private readonly gitPanel: GitPanel;
  private readonly settingsPanel: SettingsPanel;
  private events: EventSource | null = null;
  private readonly onSignedOut: () => void;
  private saveChain: Promise<void> = Promise.resolve();
  private readonly scheduleSave = debounce(() => void this.queueSave(), AUTOSAVE_MS);
  private readonly schedulePreview = debounce(() => this.updatePreview(), 160);
  private readonly scheduleVaultRefresh = debounce(() => void this.vaultChanged(), 500);
  private readonly scheduleHistoryRefresh = debounce(() => void this.historyPane.refresh(), 250);

  constructor(me: AuthMe, root: HTMLElement, onSignedOut: () => void) {
    this.root = root;
    this.onSignedOut = onSignedOut;
    this.user = me.user;
    this.vaultName = me.vault;

    this.vaultEl = h('span', { class: 'vault-name', id: 'vault-name', text: me.vault });
    this.revEl = h('span', { class: 'rev-indicator', id: 'rev-indicator', text: 'rev 0' });
    this.saveEl = h('span', { class: 'save-state', id: 'save-state', text: SAVE_LABELS.none });

    this.previewEl = h('div', { class: 'preview', id: 'preview' });
    this.editorHost = h('div', { class: 'editor-host', id: 'editor-host' });
    this.noteTitleEl = h('h1', { class: 'note-title', id: 'note-title', text: 'No note open' });
    this.notePathEl = h('span', { class: 'note-path', id: 'note-path', text: '' });
    this.centerEl = h('main', { class: 'center', id: 'center' });

    this.tree = new FileTree(this);
    this.searchPane = new SearchPane(this);
    this.tagsPane = new TagsPane(this);
    this.backlinksPane = new LinkPane(this, 'No backlinks yet.');
    this.outlinksPane = new LinkPane(this, 'This note links to nothing yet.');
    this.historyPane = new HistoryPane(this);
    this.graph = new GraphView((path) => void this.open(path));

    this.leftPanel = new TabbedPanel(
      [
        { id: 'files', label: 'Files', element: this.tree.el },
        { id: 'search', label: 'Search', element: this.searchPane.el, onShow: () => this.searchPane.focus() },
        { id: 'tags', label: 'Tags', element: this.tagsPane.el },
      ],
      'vaultsync.leftTab',
    );
    this.rightPanel = new TabbedPanel(
      [
        { id: 'backlinks', label: 'Backlinks', element: this.backlinksPane.el },
        { id: 'outgoing', label: 'Outgoing', element: this.outlinksPane.el },
        { id: 'graph', label: 'Graph', element: this.graph.el, onShow: () => this.graph.refreshTheme() },
        { id: 'history', label: 'History', element: this.historyPane.el, onShow: () => void this.historyPane.refresh() },
      ],
      'vaultsync.rightTab',
    );

    this.editor = new MarkdownEditor(this.editorHost, {
      onChange: () => this.onEditorChange(),
      onBlur: () => void this.queueSave(),
      onSave: () => void this.queueSave(),
      onQuickOpen: () => this.quickSwitcher.open(),
      onSearch: () => this.focusSearch(),
      wikiTargets: () => this.filePaths,
      tagNames: () => this.tagList.map((tag) => tag.tag),
    });
    this.quickSwitcher = new QuickSwitcher(this);
    this.gitPanel = new GitPanel(this);
    this.settingsPanel = new SettingsPanel(this);

    this.theme = this.readStored<ThemeMode>(THEME_KEY, ['system', 'light', 'dark'], 'system');
    this.viewMode = this.readStored<ViewMode>(VIEW_KEY, ['edit', 'preview', 'split'], 'split');

    this.applyTheme();
    this.buildLayout();
    this.applyViewMode();
    this.renderHeaderMeta();
    this.bindShortcuts();
    window.addEventListener('beforeunload', this.onBeforeUnload);
  }

  /** Tears the SPA down (used when signing out without a page reload). */
  destroy(): void {
    this.events?.close();
    this.events = null;
    document.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    this.graph.destroy();
    this.editor.destroy();
  }

  private readStored<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
    const value = window.localStorage.getItem(key);
    return allowed.find((candidate) => candidate === value) ?? fallback;
  }

  private buildLayout(): void {
    const themeButton = h('button', {
      class: 'icon-button',
      id: 'theme-toggle',
      type: 'button',
      title: 'Toggle theme',
      text: this.theme === 'dark' ? '☾' : this.theme === 'light' ? '☀' : '◐',
      on: { click: () => this.cycleTheme() },
    });
    const header = h('header', { class: 'topbar', id: 'topbar' }, [
      h('div', { class: 'brand' }, [h('span', { class: 'brand-mark', text: '◆' }), h('span', { text: 'VaultSync' })]),
      this.vaultEl,
      this.revEl,
      this.saveEl,
      h('div', { class: 'spacer' }),
      this.viewModeControls(),
      themeButton,
      h('button', {
        class: 'icon-button',
        id: 'git-button',
        type: 'button',
        title: 'Git sync',
        text: '⑂',
        on: { click: () => this.gitPanel.open() },
      }),
      h('button', {
        class: 'icon-button',
        id: 'settings-button',
        type: 'button',
        title: 'Settings',
        text: '⚙',
        on: { click: () => this.settingsPanel.open() },
      }),
      h('button', {
        class: 'icon-button',
        id: 'logout-button',
        type: 'button',
        title: `Sign out ${this.user ?? ''}`.trim(),
        text: '⏻',
        on: { click: () => void this.logout() },
      }),
    ]);

    this.centerEl.append(
      h('div', { class: 'note-header' }, [
        h('div', { class: 'note-heading' }, [this.noteTitleEl, this.notePathEl]),
        h('div', { class: 'note-actions' }, [
          h('button', {
            class: 'ghost-button small',
            id: 'new-note-button',
            text: 'New note',
            type: 'button',
            on: { click: () => void createNote(this, '') },
          }),
          h('button', {
            class: 'ghost-button small',
            id: 'save-button',
            text: 'Save',
            type: 'button',
            on: { click: () => void this.queueSave() },
          }),
        ]),
      ]),
      h('div', { class: 'editor-wrap' }, [this.editorHost, this.previewEl]),
    );

    this.previewEl.addEventListener('click', (event) => this.onPreviewClick(event));
    this.centerEl.addEventListener('dragover', (event) => {
      if (event.dataTransfer?.types.includes('Files')) event.preventDefault();
    });
    this.centerEl.addEventListener('drop', (event) => {
      if (!event.dataTransfer?.types.includes('Files')) return;
      event.preventDefault();
      void this.uploadIntoVault(Array.from(event.dataTransfer.files));
    });

    this.root.replaceChildren(
      h('div', { class: 'app', id: 'app-shell' }, [
        header,
        h('div', { class: 'workspace' }, [
          h('aside', { class: 'sidebar left', id: 'left-sidebar' }, [this.leftPanel.el]),
          this.centerEl,
          h('aside', { class: 'sidebar right', id: 'right-sidebar' }, [this.rightPanel.el]),
        ]),
      ]),
    );
  }

  private viewModeControls(): HTMLElement {
    const modes: ViewMode[] = ['edit', 'preview', 'split'];
    const buttons = modes.map((mode) => {
      const button = h('button', {
        class: 'segmented-button',
        id: `view-${mode}`,
        type: 'button',
        text: mode,
        title: `${mode} mode`,
        on: { click: () => this.setViewMode(mode) },
      });
      this.viewButtons.set(mode, button);
      return button;
    });
    return h('div', { class: 'segmented', id: 'view-modes' }, buttons);
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    const mod = event.ctrlKey || event.metaKey;
    if (!mod) return;
    if (event.key.toLowerCase() === 's') {
      event.preventDefault();
      void this.queueSave();
    } else if (event.key.toLowerCase() === 'o') {
      event.preventDefault();
      this.quickSwitcher.open();
    } else if (event.shiftKey && event.key.toLowerCase() === 'f') {
      event.preventDefault();
      this.focusSearch();
    }
  };

  private readonly onBeforeUnload = (): void => {
    void this.queueSave();
  };

  private bindShortcuts(): void {
    document.addEventListener('keydown', this.onKeyDown);
  }

  // ------------------------------------------------------------------ boot

  async start(): Promise<void> {
    this.startEvents();
    await this.vaultChanged();
    this.editor.focus();
  }

  async vaultChanged(): Promise<void> {
    try {
      const [treeResponse, tagResponse, stats] = await Promise.all([api.tree(), api.tags(), api.stats()]);
      this.filePaths = flattenTree(treeResponse.tree);
      this.wikiIndex = buildWikiIndex(this.filePaths);
      this.tagList = tagResponse.tags;
      this.rev = stats.rev;
      await this.tree.refresh();
      this.tagsPane.setTags(this.tagList);
      this.refreshGraph();
      this.searchPane.refreshIfActive();
      this.renderHeaderMeta();
      await this.refreshRightPanes();
    } catch (error) {
      toast(`Refresh failed: ${(error as Error).message}`, { kind: 'error' });
    }
  }

  private async refreshRightPanes(): Promise<void> {
    const path = this.current;
    if (!path) {
      this.backlinksPane.setRefs([]);
      this.outlinksPane.setRefs([]);
      void this.historyPane.refresh();
      return;
    }
    try {
      const links = await api.links(path);
      if (this.current !== path) return;
      this.backlinksPane.setRefs(links.backlinks);
      this.outlinksPane.setRefs(links.outlinks);
    } catch {
      this.backlinksPane.setRefs([]);
      this.outlinksPane.setRefs([]);
    }
    void this.historyPane.refresh();
  }

  refreshGraph(): void {
    void api
      .graph()
      .then((data) => this.graph.setData(data))
      .catch(() => undefined);
  }

  // ------------------------------------------------------------- editor IO

  async open(path: string): Promise<void> {
    if (path === this.current) {
      this.editor.focus();
      return;
    }
    await this.queueSave();
    try {
      const file = await api.readFile(path);
      this.adopt(file);
      toast(`Opened ${path}`, { timeoutMs: 1500 });
    } catch (error) {
      if (error instanceof ApiError && error.status === 415) {
        toast(`${path} is not a UTF-8 text file`, { kind: 'error' });
      } else {
        toast(`Open failed: ${(error as Error).message}`, { kind: 'error' });
      }
    }
  }

  /** Loads a file body into the editor without touching the server again. */
  private adopt(file: FileResponse): void {
    this.current = file.path;
    this.baseHash = file.hash;
    this.savedContent = file.content;
    this.editor.setDoc(file.content);
    this.setSaveState('saved');
    this.renderHeaderMeta();
    this.updatePreview();
    this.tree.refresh().catch(() => undefined);
    void this.refreshRightPanes();
    this.editor.scrollToTop();
  }

  async reloadCurrent(): Promise<void> {
    const path = this.current;
    if (!path) return;
    try {
      const file = await api.readFile(path);
      this.adopt(file);
      toast(`Reloaded ${path}`, { kind: 'success' });
    } catch (error) {
      toast(`Reload failed: ${(error as Error).message}`, { kind: 'error' });
    }
  }

  closeCurrent(reason: string): void {
    this.current = null;
    this.baseHash = null;
    this.savedContent = '';
    this.editor.setDoc('');
    this.setSaveState('none');
    this.renderHeaderMeta();
    this.updatePreview();
    toast(`${reason} — no note open`, { timeoutMs: 2500 });
  }

  setCurrentPath(path: string): void {
    this.current = path;
    this.renderHeaderMeta();
  }

  setVaultName(name: string): void {
    if (!name) return;
    this.vaultName = name;
    this.vaultEl.textContent = name;
    document.title = `${name} · VaultSync`;
  }

  private onEditorChange(): void {
    const dirty = this.editor.getDoc() !== this.savedContent;
    this.setSaveState(dirty ? 'unsaved' : 'saved');
    if (dirty) this.scheduleSave();
    this.schedulePreview();
  }

  private queueSave(): Promise<void> {
    this.saveChain = this.saveChain.then(() => this.performSave()).catch(() => undefined);
    return this.saveChain;
  }

  private async performSave(): Promise<void> {
    const path = this.current;
    if (!path) return;
    const content = this.editor.getDoc();
    if (content === this.savedContent) {
      if (this.saveState === 'unsaved' || this.saveState === 'saving') this.setSaveState('saved');
      return;
    }
    this.setSaveState('saving');
    try {
      const result = await api.writeFile(path, content, this.baseHash);
      this.noteLocalRev(result.rev);
      this.baseHash = result.hash;
      this.savedContent = content;
      this.rev = Math.max(this.rev, result.rev);
      if (this.editor.getDoc() !== content) {
        this.setSaveState('unsaved');
        this.scheduleSave();
      } else {
        this.setSaveState('saved');
      }
      this.renderHeaderMeta();
      this.updatePreview();
      this.scheduleHistoryRefresh();
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        this.setSaveState('conflict');
        await this.resolveConflict(path);
      } else {
        this.setSaveState('unsaved');
        toast(`Save failed: ${(error as Error).message}`, { kind: 'error' });
      }
    }
  }

  private async resolveConflict(path: string): Promise<void> {
    const choice = await showConflictDialog(path, this.editor.getDoc());
    if (choice === null) return;
    if (choice === 'theirs') {
      await this.reloadCurrent();
      return;
    }
    const content = this.editor.getDoc();
    try {
      const result = await api.writeFile(path, content, null);
      this.noteLocalRev(result.rev);
      this.baseHash = result.hash;
      this.savedContent = content;
      this.rev = Math.max(this.rev, result.rev);
      this.setSaveState('saved');
      this.renderHeaderMeta();
      this.updatePreview();
      this.scheduleHistoryRefresh();
      toast('Overwrote the server copy with your version', { kind: 'success' });
    } catch (error) {
      this.setSaveState('unsaved');
      toast(`Overwrite failed: ${(error as Error).message}`, { kind: 'error' });
    }
  }

  // ---------------------------------------------------------------- render

  private setSaveState(state: SaveState): void {
    this.saveState = state;
    this.saveEl.textContent = SAVE_LABELS[state];
    this.saveEl.className = `save-state state-${state}`;
  }

  private renderHeaderMeta(): void {
    this.revEl.textContent = `rev ${this.rev}`;
    this.vaultEl.textContent = this.vaultName;
    document.title = this.current ? `${this.current.split('/').pop() ?? ''} · ${this.vaultName}` : `${this.vaultName} · VaultSync`;
    this.noteTitleEl.textContent = this.current ? (this.current.split('/').pop() ?? this.current) : 'No note open';
    this.notePathEl.textContent = this.current ?? 'Ctrl+O to switch notes';
  }

  private updatePreview(): void {
    const html = renderMarkdown(this.editor.getDoc(), {
      path: this.current ?? '',
      index: this.wikiIndex,
      rawUrl: (path) => api.rawUrl(path),
    });
    this.previewEl.innerHTML = html;
  }

  private onPreviewClick(event: MouseEvent): void {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const link = target.closest('a');
    if (!link) return;
    const vaultPath = link.dataset['vaultPath'];
    const wiki = link.dataset['wikilink'];
    const tag = link.dataset['tag'];
    if (tag) {
      event.preventDefault();
      this.runSearch(`#${tag}`);
      return;
    }
    if (vaultPath) {
      event.preventDefault();
      if (this.filePaths.includes(vaultPath) || this.filePaths.some((path) => path.toLowerCase() === vaultPath.toLowerCase())) {
        void this.open(vaultPath);
      } else {
        void this.createNoteWithName(vaultPath);
      }
      return;
    }
    if (wiki) {
      event.preventDefault();
      const name = wiki.replace(/\.(md|markdown)$/i, '');
      toast(`“${wiki}” does not exist yet`, {
        actionLabel: 'Create note',
        onAction: () => void this.createNoteWithName(name),
      });
    }
  }

  async createNoteWithName(raw: string): Promise<void> {
    const name = raw.trim().replace(/\.(md|markdown)$/i, '');
    if (!name) return;
    const path = `${name}.md`;
    if (this.filePaths.includes(path)) {
      await this.open(path);
      return;
    }
    try {
      const created = await api.writeFile(path, '', null);
      this.noteLocalRev(created.rev);
      await this.vaultChanged();
      await this.open(path);
      toast(`Created ${path}`, { kind: 'success' });
    } catch (error) {
      toast(`Create failed: ${(error as Error).message}`, { kind: 'error' });
    }
  }

  private async uploadIntoVault(files: File[]): Promise<void> {
    await uploadFiles(this, '', files);
  }

  runSearch(query: string): void {
    this.leftPanel.show('search');
    this.searchPane.setQuery(query);
  }

  private focusSearch(): void {
    this.leftPanel.show('search');
    this.searchPane.focus();
  }

  // ------------------------------------------------------------- view/theme

  setViewMode(mode: ViewMode): void {
    this.viewMode = mode;
    window.localStorage.setItem(VIEW_KEY, mode);
    this.applyViewMode();
  }

  private applyViewMode(): void {
    this.centerEl.classList.remove('mode-edit', 'mode-preview', 'mode-split');
    this.centerEl.classList.add(`mode-${this.viewMode}`);
    for (const [mode, button] of this.viewButtons) button.classList.toggle('active', mode === this.viewMode);
    this.editor.setVisible(this.viewMode !== 'preview');
    this.previewEl.hidden = this.viewMode === 'edit';
    this.updatePreview();
  }

  private cycleTheme(): void {
    const next: ThemeMode = this.theme === 'system' ? 'light' : this.theme === 'light' ? 'dark' : 'system';
    this.theme = next;
    window.localStorage.setItem(THEME_KEY, next);
    this.applyTheme();
  }

  private applyTheme(): void {
    if (this.theme === 'system') document.documentElement.removeAttribute('data-theme');
    else document.documentElement.dataset['theme'] = this.theme;
    const button = document.getElementById('theme-toggle');
    if (button) button.textContent = this.theme === 'dark' ? '☾' : this.theme === 'light' ? '☀' : '◐';
    this.graph.refreshTheme();
  }

  // ---------------------------------------------------------------- events

  private startEvents(): void {
    if (this.events) return;
    const source = new EventSource('/api/v1/events');
    this.events = source;
    source.addEventListener('rev', (event) => {
      let rev = this.rev;
      try {
        const payload = JSON.parse((event as MessageEvent<string>).data) as { rev?: number };
        if (typeof payload.rev === 'number') rev = payload.rev;
      } catch {
        /* malformed payload: still refresh below */
      }
      this.onRemoteRev(rev);
    });
  }

  private onRemoteRev(rev: number): void {
    if (rev <= this.rev) return;
    this.rev = rev;
    this.renderHeaderMeta();
    // The commit that produced this revision may be our own write still in
    // flight; give the response a moment to record it as local.
    window.setTimeout(() => {
      if (rev <= this.localRev) return;
      toast('Changes arrived', { timeoutMs: 2500 });
      this.scheduleVaultRefresh();
      void this.checkOpenFile();
    }, 400);
  }

  /** Records the revision produced by a local mutation (see `onRemoteRev`). */
  noteLocalRev(rev: number): void {
    this.localRev = Math.max(this.localRev, rev);
  }

  private async checkOpenFile(): Promise<void> {
    const path = this.current;
    if (!path) return;
    let remote: FileResponse;
    try {
      remote = await api.readFile(path);
    } catch {
      if (this.saveState === 'saved') this.closeCurrent('Deleted on another device');
      return;
    }
    if (this.current !== path) return;
    if (remote.hash === this.baseHash) return;
    if (remote.content === this.editor.getDoc()) {
      // Same bytes we already hold (usually our own just-committed save):
      // adopt the new hash without disturbing the editor or the cursor.
      this.baseHash = remote.hash;
      this.savedContent = remote.content;
      if (this.saveState !== 'conflict') this.setSaveState('saved');
      return;
    }
    if (this.saveState !== 'saved') {
      toast(`${path} changed elsewhere — your edits are kept`, {
        actionLabel: 'Reload theirs',
        onAction: () => void this.reloadCurrent(),
      });
      return;
    }
    this.adopt(remote);
  }

  private async logout(): Promise<void> {
    const ok = await confirmDialog('Sign out', 'Sign out of this browser session?', 'Sign out');
    if (!ok) return;
    await this.queueSave();
    try {
      await api.logout();
    } catch (error) {
      toast(`Sign out failed: ${(error as Error).message}`, { kind: 'error' });
    }
    this.destroy();
    this.root.replaceChildren();
    this.onSignedOut();
  }
}
